import { c as _c } from "react/compiler-runtime";
import { ChartDot } from '#components/chart/components/ChartDot';
import { HighlightedArea } from '#components/chart/components/HighlightedArea';
import { LineLabel } from '#components/chart/components/LineLabel';
import { TooltipContent } from '#components/chart/components/TooltipContent';
import { VerticalCursor } from '#components/chart/components/VerticalCursor';
import { XAxisLabel } from '#components/chart/components/XAxisLabel';
import { useChartContext } from '#components/chart/context/useChartContext';
import { formatNumber, TargetValueIndicator } from '#components/chart/utils';
import { cn } from '#lib/utils';
import { CartesianGrid, Label, Line, LineChart as RechartsLineChart, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useIsClient } from 'usehooks-ts';
export function LineChart() {
  const $ = _c(110);
  const {
    data,
    options: t0,
    isCalculated
  } = useChartContext();
  let colors;
  let options;
  let size;
  let t1;
  if ($[0] !== t0) {
    ({
      colors,
      size,
      showUnit: t1,
      ...options
    } = t0);
    $[0] = t0;
    $[1] = colors;
    $[2] = options;
    $[3] = size;
    $[4] = t1;
  } else {
    colors = $[1];
    options = $[2];
    size = $[3];
    t1 = $[4];
  }
  const showUnit = t1 === undefined ? true : t1;
  const isClient = useIsClient();
  const {
    chartData,
    targetPeriodConfig,
    realPeriodConfig,
    min,
    max,
    hasRealData,
    minYAxisTick,
    yAxisTicks,
    targetLevelDescription,
    positiveDirection
  } = data;
  const yAxisFontSize = size === "sm" ? 10 : 12;
  const commonFontSize = size === "sm" ? 12 : 14;
  const dotLabelDy = size === "sm" ? -13 : -17;
  let t2;
  if ($[5] !== size) {
    t2 = size === "sm" ? {
      top: 28,
      right: 0,
      left: -12,
      bottom: 0
    } : {
      top: 38,
      right: 0,
      left: 2,
      bottom: 0
    };
    $[5] = size;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const margin = t2;
  const minYValue = minYAxisTick < 0 ? minYAxisTick : 0;
  const yAxisValueMaxLength = yAxisTicks[yAxisTicks.length - 1].toString().length;
  const yAxisWidth = 28 + yAxisValueMaxLength * 5;
  if (!isClient) {
    return null;
  }
  let t3;
  if ($[7] !== isCalculated) {
    t3 = cn("transition-opacity duration-100 opacity-0", {
      "opacity-100": isCalculated
    });
    $[7] = isCalculated;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== colors.GRID_LINE) {
    t4 = <CartesianGrid vertical={false} stroke={colors.GRID_LINE} />;
    $[9] = colors.GRID_LINE;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = hasRealData ? realPeriodConfig.max : min;
  let t6;
  if ($[11] !== minYValue || $[12] !== t5) {
    t6 = {
      x: t5,
      y: minYValue
    };
    $[11] = minYValue;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== max || $[15] !== minYValue) {
    t7 = {
      x: max,
      y: minYValue
    };
    $[14] = max;
    $[15] = minYValue;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = [t6, t7];
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== colors.XAXIS_LINE || $[21] !== t8) {
    t9 = <ReferenceLine ifOverflow="visible" stroke={colors.XAXIS_LINE} strokeWidth={2} strokeDasharray="2 4" segment={t8} />;
    $[20] = colors.XAXIS_LINE;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== colors.XAXIS_LINE || $[24] !== hasRealData || $[25] !== min || $[26] !== minYValue || $[27] !== realPeriodConfig) {
    t10 = hasRealData && <ReferenceLine ifOverflow="visible" strokeWidth={2} stroke={colors.XAXIS_LINE} segment={[{
      x: min,
      y: minYValue
    }, {
      x: realPeriodConfig.max,
      y: minYValue
    }]} />;
    $[23] = colors.XAXIS_LINE;
    $[24] = hasRealData;
    $[25] = min;
    $[26] = minYValue;
    $[27] = realPeriodConfig;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <HighlightedArea />;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const t12 = data.targetPeriodConfig.descriptorPoint?.targetValueIndicator ? "4 4" : undefined;
  const t13 = data.targetPeriodConfig.descriptorPoint?.y;
  let t14;
  if ($[30] !== colors.REFERENCE_LINE || $[31] !== t12 || $[32] !== t13) {
    t14 = <ReferenceLine strokeDasharray={t12} y={t13} stroke={colors.REFERENCE_LINE} />;
    $[30] = colors.REFERENCE_LINE;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Tooltip isAnimationActive={false} content={TooltipContent} cursor={<VerticalCursor />} />;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== colors.XAXIS_LINE) {
    t16 = {
      stroke: colors.XAXIS_LINE,
      strokeWidth: 2
    };
    $[35] = colors.XAXIS_LINE;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== chartData || $[38] !== colors.TARGET_AXIS_ACTIVE_TEXT || $[39] !== colors.TARGET_AXIS_TEXT || $[40] !== colors.XAXIS_TEXT || $[41] !== commonFontSize || $[42] !== hasRealData || $[43] !== realPeriodConfig || $[44] !== targetPeriodConfig.descriptorPoint?.x) {
    t17 = <XAxisLabel textProps={t18 => {
      const {
        value,
        index
      } = t18;
      const item = chartData[index];
      const config = {
        hideParent: item.show === false,
        forceShow: item.forceShow,
        hideLabel: item.hideLabel,
        fontSize: commonFontSize,
        hideTick: item.targetValueIndicator != null && item.xAxis != targetPeriodConfig.descriptorPoint?.x
      };
      if (hasRealData && value <= realPeriodConfig.max) {
        return {
          ...config,
          fill: colors.XAXIS_TEXT,
          fontWeight: value === realPeriodConfig.max ? "bold" : "normal"
        };
      }
      if (value === targetPeriodConfig.descriptorPoint?.x) {
        return {
          ...config,
          fill: colors.TARGET_AXIS_ACTIVE_TEXT,
          fontWeight: "bold"
        };
      }
      return {
        ...config,
        fill: colors.TARGET_AXIS_TEXT,
        fontWeight: "normal"
      };
    }} />;
    $[37] = chartData;
    $[38] = colors.TARGET_AXIS_ACTIVE_TEXT;
    $[39] = colors.TARGET_AXIS_TEXT;
    $[40] = colors.XAXIS_TEXT;
    $[41] = commonFontSize;
    $[42] = hasRealData;
    $[43] = realPeriodConfig;
    $[44] = targetPeriodConfig.descriptorPoint?.x;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t16 || $[47] !== t17) {
    t18 = <XAxis interval={0} xAxisId={0} dataKey="xAxis" axisLine={false} tickSize={6} tickLine={t16} tick={t17} />;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== colors.YAXIS_TEXT || $[50] !== yAxisFontSize) {
    t19 = {
      fill: colors.YAXIS_TEXT,
      fontSize: yAxisFontSize
    };
    $[49] = colors.YAXIS_TEXT;
    $[50] = yAxisFontSize;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const t20 = size === "sm" && yAxisTicks.length > 6 ? "preserveEnd" : 0;
  const t21 = size === "sm" ? 0 : 4;
  let t22;
  if ($[52] !== minYValue) {
    t22 = minYValue < 0 ? ["dataMin", "dataMax"] : [0, "dataMax"];
    $[52] = minYValue;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== colors.UNIT_TEXT || $[55] !== data.unit || $[56] !== showUnit || $[57] !== yAxisWidth) {
    t23 = showUnit && <Label fill={colors.UNIT_TEXT} fontSize={14} value={data.unit} dy={-25} dx={yAxisWidth} position="insideTopLeft" />;
    $[54] = colors.UNIT_TEXT;
    $[55] = data.unit;
    $[56] = showUnit;
    $[57] = yAxisWidth;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t19 || $[60] !== t20 || $[61] !== t21 || $[62] !== t22 || $[63] !== t23 || $[64] !== yAxisTicks || $[65] !== yAxisWidth) {
    t24 = <YAxis tick={t19} axisLine={false} tickLine={false} interval={t20} tickMargin={t21} ticks={yAxisTicks} domain={t22} tickFormatter={formatNumber} width={yAxisWidth}>{t23}</YAxis>;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
    $[64] = yAxisTicks;
    $[65] = yAxisWidth;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== chartData || $[68] !== colors.CURRENT_DOT_FILL || $[69] !== colors.CURRENT_LINE || $[70] !== commonFontSize || $[71] !== dotLabelDy || $[72] !== hasRealData || $[73] !== realPeriodConfig) {
    t25 = hasRealData && <Line isAnimationActive={false} type="linear" dataKey="realValue" strokeWidth={3} activeDot={false} dot={<ChartDot type="real" fill={colors.CURRENT_DOT_FILL} isActive={payload => payload.xAxis === realPeriodConfig.max} />} label={<LineLabel stroke={colors.CURRENT_LINE} fill={colors.CURRENT_LINE} textProps={t26 => {
      const {
        index: index_0
      } = t26;
      const item_0 = chartData[index_0];
      if (item_0.isInterpolated) {
        return {
          hideLabel: true
        };
      }
      return {
        forceShow: item_0.forceShow,
        dy: dotLabelDy,
        fontSize: commonFontSize,
        fontWeight: item_0.xAxis === realPeriodConfig.max ? "normal" : "lighter"
      };
    }} />} />;
    $[67] = chartData;
    $[68] = colors.CURRENT_DOT_FILL;
    $[69] = colors.CURRENT_LINE;
    $[70] = commonFontSize;
    $[71] = dotLabelDy;
    $[72] = hasRealData;
    $[73] = realPeriodConfig;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== targetPeriodConfig.descriptorPoint?.x) {
    t26 = <ChartDot type="target" isActive={payload_0 => payload_0.xAxis === targetPeriodConfig.descriptorPoint?.x} />;
    $[75] = targetPeriodConfig.descriptorPoint?.x;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== chartData || $[78] !== colors.TARGET_ACTIVE_DOT || $[79] !== colors.TARGET_DOTS || $[80] !== commonFontSize || $[81] !== dotLabelDy || $[82] !== positiveDirection || $[83] !== targetLevelDescription || $[84] !== targetPeriodConfig.descriptorPoint?.x) {
    t27 = <LineLabel textProps={t28 => {
      const {
        index: index_1
      } = t28;
      const item_1 = chartData[index_1];
      const config_0 = {
        forceShow: item_1.forceShow,
        dy: dotLabelDy,
        fontSize: commonFontSize
      };
      if (item_1.realValue === item_1.targetValue || item_1.targetValueIndicator) {
        let text = undefined;
        bb120: switch (item_1.targetValueIndicator) {
          case TargetValueIndicator.Increases:
            {}
          case TargetValueIndicator.Decreases:
            {
              text = positiveDirection === "bigger_better" ? "Min" : "Max";
              break bb120;
            }
          default:
            {}
        }
        return {
          ...config_0,
          descriptorText: targetLevelDescription || text || undefined,
          stroke: colors.TARGET_ACTIVE_DOT,
          fill: colors.TARGET_ACTIVE_DOT,
          fontWeight: "normal"
        };
      }
      if (targetPeriodConfig.descriptorPoint?.x === item_1.xAxis) {
        return {
          ...config_0,
          descriptorText: targetLevelDescription || undefined,
          stroke: colors.TARGET_ACTIVE_DOT,
          fill: colors.TARGET_ACTIVE_DOT,
          fontWeight: "normal"
        };
      }
      return {
        ...config_0,
        stroke: colors.TARGET_DOTS,
        fill: colors.TARGET_DOTS,
        fontWeight: "lighter"
      };
    }} />;
    $[77] = chartData;
    $[78] = colors.TARGET_ACTIVE_DOT;
    $[79] = colors.TARGET_DOTS;
    $[80] = commonFontSize;
    $[81] = dotLabelDy;
    $[82] = positiveDirection;
    $[83] = targetLevelDescription;
    $[84] = targetPeriodConfig.descriptorPoint?.x;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[86] !== colors.TARGET_LINE || $[87] !== t26 || $[88] !== t27) {
    t28 = <Line isAnimationActive={false} type="linear" dataKey="targetValue" stroke={colors.TARGET_LINE} fill={colors.TARGET_LINE} activeDot={false} strokeDasharray="3 3" dot={t26} label={t27} />;
    $[86] = colors.TARGET_LINE;
    $[87] = t26;
    $[88] = t27;
    $[89] = t28;
  } else {
    t28 = $[89];
  }
  let t29;
  if ($[90] !== colors.TARGET_ACTIVE_DOT || $[91] !== minYValue || $[92] !== targetPeriodConfig.descriptorPoint) {
    t29 = targetPeriodConfig.descriptorPoint && <ReferenceDot x={targetPeriodConfig.descriptorPoint.x} y={minYValue} stroke={colors.TARGET_ACTIVE_DOT} shape={<ChartDot ignoreValue={true} />} />;
    $[90] = colors.TARGET_ACTIVE_DOT;
    $[91] = minYValue;
    $[92] = targetPeriodConfig.descriptorPoint;
    $[93] = t29;
  } else {
    t29 = $[93];
  }
  let t30;
  if ($[94] !== chartData || $[95] !== margin || $[96] !== t10 || $[97] !== t14 || $[98] !== t18 || $[99] !== t24 || $[100] !== t25 || $[101] !== t28 || $[102] !== t29 || $[103] !== t4 || $[104] !== t9) {
    t30 = <RechartsLineChart data={chartData} margin={margin}>{t4}{t9}{t10}{t11}{t14}{t15}{t18}{t24}{t25}{t28}{t29}</RechartsLineChart>;
    $[94] = chartData;
    $[95] = margin;
    $[96] = t10;
    $[97] = t14;
    $[98] = t18;
    $[99] = t24;
    $[100] = t25;
    $[101] = t28;
    $[102] = t29;
    $[103] = t4;
    $[104] = t9;
    $[105] = t30;
  } else {
    t30 = $[105];
  }
  let t31;
  if ($[106] !== options || $[107] !== t3 || $[108] !== t30) {
    t31 = <ResponsiveContainer {...options} className={t3}>{t30}</ResponsiveContainer>;
    $[106] = options;
    $[107] = t3;
    $[108] = t30;
    $[109] = t31;
  } else {
    t31 = $[109];
  }
  return t31;
}